import PlayIcon from "@/assets/FFFFFF_play_icon.svg?react";
import CarouselAlbumItem from "@/components/Campaign/management/components/CarouselAlbumItem";
import CarouselFooter from "@/components/Campaign/management/components/CarouselFooter";
import ContentLikeButton from "@/components/Campaign/management/components/ContentLikeButton";
import useCampaignManagementStore from "@/store/campaignManagement";
import {type TCarouselMedia} from "@/store/carousel";
import {shouldMuteVideosAtom} from "@/store/jotaiAtoms";
import type {TInstagramMedia} from "@/store/types";
import {useAtom} from "jotai";
import {type FC, memo, useCallback, useMemo, useRef, useState} from "react";
import {useDebounce, useEvent, useHoverDirty} from "react-use";

const CarouselItem: FC<{
  content: TCarouselMedia;
  isCurrentlyVisible: boolean;
}> = ({content, isCurrentlyVisible}) => {
  const hasMoreChildren = useMemo(() => (content.instagram_media?.children?.length ?? 0) > 1, [content]);
  const [shouldMuteVideos, setShouldMuteVideos] = useAtom(shouldMuteVideosAtom);
  const isGalleryTab = useCampaignManagementStore(state => state.tabState === "gallery");

  const videoRef = useRef<HTMLDivElement>(null!);
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [hoveredAlbumItem, setHoveredAlbumItem] = useState<TInstagramMedia | null>(null);
  const nonDebouncedVideoHover = useHoverDirty(videoRef);
  useDebounce(() => setIsVideoHovered(nonDebouncedVideoHover && isCurrentlyVisible), 100, [
    nonDebouncedVideoHover,
    isCurrentlyVisible,
  ]);
  const handleFocus = useCallback(() => {
    setIsVideoHovered(false);
    setHoveredAlbumItem(null);
  }, []);

  useEvent("blur-sm", handleFocus);

  return (
    <div
      key={content.id}
      className={`flex grow flex-col overflow-hidden ${!!content.caption ? "mx-2.5 sm:mx-auto" : ""}`}>
      <div
        className="flex h-screen flex-col items-center justify-center"
        onContextMenu={e => {
          e.preventDefault();
        }}>
        <div className="flex flex-col justify-center gap-7 md:flex-row">
          <div className="flex w-fit flex-col items-center">
            {content.instagram_media?.media_type === "IMAGE" ||
            !content.instagram_media ||
            !content.instagram_media.media_file ? (
              hoveredAlbumItem && isCurrentlyVisible ? (
                <div className="flex grow flex-col items-center justify-center">
                  <div className="relative flex items-center justify-center">
                    <CarouselAlbumItem content={hoveredAlbumItem} setHoveredAlbumItem={setHoveredAlbumItem} />
                    {isGalleryTab && <ContentLikeButton content={content} />}
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={content.instagram_media?.media_file ?? content.instagram_media?.thumbnail_file!}
                    className="max-h-[60vh] rounded-lg object-contain"
                  />
                  {isGalleryTab && <ContentLikeButton content={content} />}
                </div>
              )
            ) : content.instagram_media.media_type === "VIDEO" ? (
              <div ref={videoRef} className="relative flex justify-center">
                {!isVideoHovered && (
                  <div className="border-neutral-white-dark bg-neutral-white-dark/20 absolute top-1/2 left-1/2 z-10 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border">
                    <PlayIcon className="h-10 w-10 pl-1.5" />
                  </div>
                )}
                {!isVideoHovered ? (
                  content.instagram_media.thumbnail_file ? (
                    <div className="relative">
                      <img
                        className="max-h-[60vh] rounded-lg object-cover"
                        src={content.instagram_media.thumbnail_file}
                      />
                      {isGalleryTab && <ContentLikeButton content={content} />}
                    </div>
                  ) : (
                    <div className="size-[400px] bg-black" />
                  )
                ) : (
                  <div className="relative">
                    <video
                      autoPlay
                      loop
                      controls
                      muted={shouldMuteVideos}
                      onVolumeChange={e => setShouldMuteVideos(e.currentTarget.muted || e.currentTarget.volume === 0)}
                      controlsList="nodownload"
                      src={content.instagram_media.media_file!}
                      className="max-h-[60vh] rounded-lg object-contain"
                    />
                    {isGalleryTab && <ContentLikeButton content={content} />}
                  </div>
                )}
              </div>
            ) : content.instagram_media.media_type === "PDF" ? (
              <iframe src={content.instagram_media.media_file!} className="h-[60vh] sm:h-[70vh]" />
            ) : (
              <div className="flex grow flex-col items-center justify-center">
                {hoveredAlbumItem && isCurrentlyVisible && (
                  <div className="relative flex items-center justify-center">
                    <CarouselAlbumItem content={hoveredAlbumItem} setHoveredAlbumItem={setHoveredAlbumItem} />
                    {isGalleryTab && <ContentLikeButton content={content} />}
                  </div>
                )}
              </div>
            )}
          </div>
          {!!content.caption && (
            <div className="scrollbar-hide flex max-h-36 max-w-[400px] flex-col overflow-y-auto select-text sm:pr-0 md:max-h-[60vh] md:w-5/12">
              <span className="text-xs text-white sm:text-sm">{content.caption}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col self-stretch">
          <div className="mt-4 flex items-center justify-center gap-x-4">
            {hasMoreChildren &&
              content.instagram_media?.children?.map((child, index) => (
                <CarouselAlbumItem key={index} content={child} setHoveredAlbumItem={setHoveredAlbumItem} isHoverable />
              ))}
          </div>
          <CarouselFooter content={content} />
        </div>
      </div>
    </div>
  );
};

export default memo(CarouselItem);
